import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Container, Typography, Card, CardContent, Button, Box, Grid,
  LinearProgress, List, ListItem, ListItemText, Chip, Snackbar, Alert, Paper, Table, TableBody, TableCell, 
  TableContainer, TableHead, TableRow, IconButton, FormControl, InputLabel, Select, MenuItem, Divider
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import FolderIcon from '@mui/icons-material/Folder';
import DownloadIcon from '@mui/icons-material/Download';
import AudiotrackIcon from '@mui/icons-material/Audiotrack';
import MergeIcon from '@mui/icons-material/Merge';
import BatchPredictionIcon from '@mui/icons-material/BatchPrediction';
import { formatFileSize, formatDate } from "../utils";
import HistoryTable from "../components/HistoryTable";
import * as api from "../services/api";

const VideoToolsView = ({ latestWebSocketMessage, isConnected }) => {
  const [videoFile, setVideoFile] = useState(null);
  const [audioFile, setAudioFile] = useState(null);
  const [folderSelected, setFolderSelected] = useState(false);
  const [currentOperation, setCurrentOperation] = useState('extract_audio');
  const [videoQueue, setVideoQueue] = useState([]);
  const [videoProgress, setVideoProgress] = useState({});
  const [videoHistory, setVideoHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  // Operaciones disponibles
  const operations = [
    { value: 'extract_audio', label: 'Extraer Audio de Video', icon: <AudiotrackIcon fontSize="small" /> },
    { value: 'merge_video_audio', label: 'Unir Video con Audio', icon: <MergeIcon fontSize="small" /> },
    { value: 'batch_process', label: 'Procesar Carpeta de Videos', icon: <BatchPredictionIcon fontSize="small" /> }
  ];

  // Función para buscar la cola de procesamiento de video
  const fetchVideoQueue = useCallback(async () => {
    try {
      // Este endpoint aún no existe, deberá implementarse en el backend
      const response = await axios.get('/api/video/queue');
      setVideoQueue(response.data);
    } catch (err) {
      console.error("Error al obtener cola de procesamiento de video:", err);
      if (err.response?.status === 404) {
        // El endpoint no existe aún
        console.log("El endpoint para cola de procesamiento de video aún no está implementado");
        setVideoQueue([]);
      } else {
        setError("Error al cargar la cola de procesamiento de video");
      }
    }
  }, []);

  // Función para buscar el historial de procesamiento de video
  const fetchVideoHistory = useCallback(async () => {
    try {
      // Este endpoint aún no existe, deberá implementarse en el backend
      const response = await axios.get('/api/video/history');
      setVideoHistory(response.data);
    } catch (err) {
      console.error("Error al obtener historial de procesamiento de video:", err);
      if (err.response?.status === 404) {
        // El endpoint no existe aún
        console.log("El endpoint para historial de procesamiento de video aún no está implementado");
        setVideoHistory([]);
      } else {
        setError("Error al cargar el historial de procesamiento de video");
      }
    }
  }, []);

  // Carga inicial de datos
  useEffect(() => {
    fetchVideoQueue();
    fetchVideoHistory();
    
    // Configura un intervalo para actualizar si no usamos WebSockets para esto
    const intervalId = setInterval(() => {
      fetchVideoQueue();
    }, 10000); // Cada 10 segundos
    
    return () => clearInterval(intervalId);
  }, [fetchVideoQueue, fetchVideoHistory]);

  // Responder a mensajes WebSocket
  useEffect(() => {
    if (latestWebSocketMessage) {
      if (latestWebSocketMessage.type === 'videoProgress') {
        setVideoProgress(prev => ({
          ...prev,
          [latestWebSocketMessage.filename]: latestWebSocketMessage.data
        }));
      } else if (latestWebSocketMessage.type === 'videoQueueUpdate') {
        fetchVideoQueue();
      } else if (latestWebSocketMessage.type === 'videoComplete') {
        setVideoProgress(prev => ({
          ...prev,
          [latestWebSocketMessage.filename]: { 
            ...prev[latestWebSocketMessage.filename],
            completed: true 
          }
        }));
        fetchVideoHistory();
        fetchVideoQueue();
        setSuccessMessage(`Procesamiento completado para ${latestWebSocketMessage.originalFilename}`);
      } else if (latestWebSocketMessage.type === 'videoError') {
        setError(`Error durante el procesamiento de ${latestWebSocketMessage.originalFilename}: ${latestWebSocketMessage.error}`);
      }
    }
  }, [latestWebSocketMessage, fetchVideoQueue, fetchVideoHistory]);

  // Manejar el cambio de archivo de video
  const handleVideoFileChange = (e) => {
    setVideoFile(e.target.files[0]);
    setError("");
    setSuccessMessage("");
  };

  // Manejar el cambio de archivo de audio
  const handleAudioFileChange = (e) => {
    setAudioFile(e.target.files[0]);
    setError("");
    setSuccessMessage("");
  };

  // Manejar la selección de carpeta
  const handleFolderSelect = (e) => {
    if (e.target.files.length > 0) {
      setFolderSelected(true);
      setError("");
      setSuccessMessage("");
    }
  };

  // Manejar el cambio de operación
  const handleOperationChange = (e) => {
    setCurrentOperation(e.target.value);
    setVideoFile(null);
    setAudioFile(null);
    setFolderSelected(false);
    setError("");
    setSuccessMessage("");
  };

  // Manejar la subida y procesamiento
  const handleUploadAndProcess = async (e) => {
    e.preventDefault();
    
    if (currentOperation === 'extract_audio' && !videoFile) {
      setError("Por favor selecciona un archivo de video primero.");
      return;
    } else if (currentOperation === 'merge_video_audio' && (!videoFile || !audioFile)) {
      setError("Por favor selecciona un archivo de video y un archivo de audio primero.");
      return;
    } else if (currentOperation === 'batch_process' && !folderSelected) {
      setError("Por favor selecciona una carpeta primero.");
      return;
    }

    setIsLoading(true);
    setError("");
    setSuccessMessage("");

    const formData = new FormData();
    
    if (currentOperation === 'extract_audio') {
      formData.append("video", videoFile);
      formData.append("operation", "extract_audio");
    } else if (currentOperation === 'merge_video_audio') {
      formData.append("video", videoFile);
      formData.append("audio", audioFile);
      formData.append("operation", "merge_video_audio");
    } else if (currentOperation === 'batch_process') {
      // En un caso real, necesitaríamos una API especial para manejar carpetas
      // Aquí solo simulamos el comportamiento
      formData.append("operation", "batch_process");
      formData.append("folder", "selected_folder");
    }

    try {
      // Este endpoint aún no existe, deberá implementarse en el backend
      const response = await axios.post('/api/video/process', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setSuccessMessage(response.data?.message || "Archivo subido y procesamiento iniciado.");
      setVideoFile(null);
      setAudioFile(null);
      setFolderSelected(false);
      fetchVideoQueue();
    } catch (err) {
      console.error("Error al procesar video:", err);
      if (err.response?.status === 404) {
        // El endpoint no existe aún
        setError("Esta funcionalidad aún no está implementada en el backend.");
      } else {
        setError(err.response?.data?.error || "Error al procesar el archivo de video.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Función para limpiar el historial de video
  const handleClearVideoHistory = async () => {
    try {
      await api.clearVideoHistory();
      fetchVideoHistory();
      setSuccessMessage("Historial de video limpiado correctamente");
    } catch (error) {
      console.error('Error al limpiar historial de video:', error);
      setError("Error al limpiar el historial de video");
    }
  };

  // Función para eliminar un archivo específico de video
  const handleDeleteVideoItem = async (item) => {
    try {
      await api.deleteVideoItem(item.processedFilename || item.filename);
      fetchVideoHistory();
      setSuccessMessage(`Video ${item.originalFilename} eliminado correctamente`);
    } catch (error) {
      console.error('Error al eliminar video:', error);
      setError("Error al eliminar el video");
    }
  };

  // Función para previsualizar un archivo de video procesado
  const handlePreviewVideo = async (filename) => {
    try {
      // Implementar la previsualización de video
      console.log("Previsualizar video:", filename);
      // Esta función se implementará más adelante
    } catch (error) {
      console.error('Error al previsualizar video:', error);
      setError("Error al previsualizar el video");
    }
  };

  return (
    <Container maxWidth="xl">
      <Typography variant="h5" component="h1" gutterBottom>
        Herramientas de Video
      </Typography>

      {!isConnected && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          Conectando al servidor WebSocket...
        </Alert>
      )}

      {/* Sección de selección de operación */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Seleccionar Operación</Typography>
          <FormControl fullWidth>
            <InputLabel id="operation-select-label">Operación</InputLabel>
            <Select
              labelId="operation-select-label"
              id="operation-select"
              value={currentOperation}
              label="Operación"
              onChange={handleOperationChange}
            >
              {operations.map(op => (
                <MenuItem key={op.value} value={op.value}>
                  <Box display="flex" alignItems="center">
                    {op.icon}
                    <Box ml={1}>{op.label}</Box>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </CardContent>
      </Card>

      {/* Sección de Subida */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {operations.find(op => op.value === currentOperation)?.label || "Procesar Archivo"}
          </Typography>
          <form onSubmit={handleUploadAndProcess}>
            <Grid container spacing={2} alignItems="center">
              {/* Formulario para Extraer Audio */}
              {currentOperation === 'extract_audio' && (
                <Grid item xs={12} sm={8}>
                  <Button
                    variant="outlined"
                    component="label"
                    fullWidth
                    startIcon={<CloudUploadIcon />}
                  >
                    {videoFile ? videoFile.name : "Seleccionar Archivo de Video (.mp4, .avi, ...)"}
                    <input
                      type="file"
                      hidden
                      onChange={handleVideoFileChange}
                      accept="video/mp4, video/avi, video/*"
                    />
                  </Button>
                </Grid>
              )}

              {/* Formulario para Unir Video con Audio */}
              {currentOperation === 'merge_video_audio' && (
                <>
                  <Grid item xs={12} sm={6}>
                    <Button
                      variant="outlined"
                      component="label"
                      fullWidth
                      startIcon={<CloudUploadIcon />}
                    >
                      {videoFile ? videoFile.name : "Seleccionar Archivo de Video (.mp4, .avi, ...)"}
                      <input
                        type="file"
                        hidden
                        onChange={handleVideoFileChange}
                        accept="video/mp4, video/avi, video/*"
                      />
                    </Button>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Button
                      variant="outlined"
                      component="label"
                      fullWidth
                      startIcon={<CloudUploadIcon />}
                    >
                      {audioFile ? audioFile.name : "Seleccionar Archivo de Audio (.mp3, .wav, ...)"}
                      <input
                        type="file"
                        hidden
                        onChange={handleAudioFileChange}
                        accept="audio/mpeg, audio/wav, audio/*"
                      />
                    </Button>
                  </Grid>
                  <Grid item xs={12}>
                    <Divider>
                      <Chip label="Ambos archivos son requeridos" />
                    </Divider>
                  </Grid>
                </>
              )}

              {/* Formulario para Procesar Carpeta */}
              {currentOperation === 'batch_process' && (
                <Grid item xs={12} sm={8}>
                  <Button
                    variant="outlined"
                    component="label"
                    fullWidth
                    startIcon={<FolderIcon />}
                  >
                    {folderSelected ? "Carpeta seleccionada" : "Seleccionar Carpeta con Videos"}
                    <input
                      type="file"
                      hidden
                      onChange={handleFolderSelect}
                      webkitdirectory="true"
                      directory="true"
                      multiple
                    />
                  </Button>
                </Grid>
              )}

              {/* Botón de procesamiento para todas las operaciones */}
              <Grid item xs={12} sm={currentOperation === 'merge_video_audio' ? 12 : 4}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  fullWidth
                  disabled={
                    isLoading || 
                    (currentOperation === 'extract_audio' && !videoFile) ||
                    (currentOperation === 'merge_video_audio' && (!videoFile || !audioFile)) ||
                    (currentOperation === 'batch_process' && !folderSelected)
                  }
                >
                  {isLoading ? "Procesando..." : "Procesar"}
                </Button>
              </Grid>
            </Grid>
            {isLoading && <LinearProgress sx={{mt: 2}} />}
          </form>
        </CardContent>
      </Card>

      {/* Cola de Procesamiento */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Cola de Procesamiento</Typography>
          {videoQueue.length === 0 ? (
            <Typography color="text.secondary">Cola de procesamiento vacía.</Typography>
          ) : (
            <List dense>
              {videoQueue.map((item) => {
                const progress = videoProgress[item.id || item.originalFilename] || {};
                return (
                  <ListItem key={item.id || item.originalFilename} divider>
                    <ListItemText
                      primary={item.originalFilename}
                      secondary={`${progress.status || item.status || 'En espera...'} - ${item.operation || 'Procesando'}`}
                    />
                    {progress.current && progress.total && !progress.completed && (
                      <Box sx={{ width: '50%', mr: 1 }}>
                        <LinearProgress variant="determinate" value={(progress.current / progress.total) * 100} />
                        <Typography variant="caption">{`${progress.current}/${progress.total}`}</Typography>
                      </Box>
                    )}
                    {progress.completed && <Chip label="Procesamiento Finalizado" color="info" size="small" sx={{ml: 1}} />}
                  </ListItem>
                );
              })}
            </List>
          )}
        </CardContent>
      </Card>

      {/* Historial de Procesamiento */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Historial de Procesamiento</Typography>
          {videoHistory.length === 0 ? (
            <Typography color="text.secondary">No hay historial de procesamiento disponible.</Typography>
          ) : (
            <HistoryTable 
              history={videoHistory.map(item => ({
                ...item,
                originalFilename: item.originalFilename,
                processedFilename: item.processedFilename,
                operation: item.operation === 'extract_audio' ? 'Extraer Audio' : 
                          item.operation === 'merge_video_audio' ? 'Unir Video con Audio' : 
                          item.operation === 'batch_process' ? 'Procesamiento por Lotes' : 
                          'Procesamiento',
                processedDate: item.completedDate,
                downloadLink: item.downloadLink
              }))} 
              onPreview={handlePreviewVideo}
              onClearHistory={handleClearVideoHistory}
              onDeleteItem={handleDeleteVideoItem}
              clearHistoryLabel="Limpiar historial de video"
            />
          )}
        </CardContent>
      </Card>

      {/* Snackbars para Feedback */}
      <Snackbar open={!!error} autoHideDuration={6000} onClose={() => setError('')} anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}>
        <Alert onClose={() => setError('')} severity="error" variant="filled" sx={{ width: '100%' }}>{error}</Alert>
      </Snackbar>
      <Snackbar open={!!successMessage} autoHideDuration={4000} onClose={() => setSuccessMessage('')} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert onClose={() => setSuccessMessage('')} severity="success" variant="filled" sx={{ width: '100%' }}>{successMessage}</Alert>
      </Snackbar>
    </Container>
  );
};

export default VideoToolsView; 